import { zodResolver } from "@hookform/resolvers/zod";
import InputMask from 'react-input-mask';
import {
  FormLabel,
  Input,
  FormControl,
  Grid,
  GridItem,
  Flex,
  Button,
  HStack,
  Tooltip,
  Icon,
  Text,
  InputGroup,
  InputRightAddon,
  IconButton,
  useToast,
  useDisclosure,
  ModalFooter,
  ModalBody,
  ModalContent,
  Modal,
  Box,
  ModalOverlay,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogOverlay,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialog,
  Heading
} from "@chakra-ui/react";
import FieldError from "@/modules/FakeRp/components/FieldError";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { FormValues, schema, schemaEdit } from "../model";
import { UpsertFormModal } from "@/components/UpsertFormNewDesign";
import ActiveSwitch from "@/components/components/ActiveSwitch";
import { useRef, useState } from "react";
import { FaKey, FaPrint } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import { IoIosToday, IoMdPrint } from "react-icons/io";
import { FiAlertTriangle } from "react-icons/fi";
import { useGeneratePermission } from "@/hooks/useGeneratePemission";
import { api } from "@/services/api";

interface Props {
  defaultValues?: { [x: string]: any };
  handleSave: (values: FormValues) => Promise<any>;
  isLoading?: boolean;
  title?: string;
  edit?: boolean;
  onRefetchData?: () => Promise<void>; 
}

export function Form({ isLoading = false, title, edit, onRefetchData, ...props }: Props) {
  const nav = useNavigate();
  const { user } = useAuth();
  
  let email = user?.split('@')[1];

  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const { isOpen: isQrModalOpen, onOpen: onQrModalOpen, onClose: onQrModalClose } = useDisclosure();
  const cancelRef = useRef(null);
  const qrCodeRef = useRef<HTMLDivElement>(null); 
  const toast = useToast();
  const [isSearchingCPF, setIsSearchingCPF] = useState(false);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const generatePermissionMutation = edit ? useGeneratePermission() : null;

  const handleGeneratecodigoPermissao = async (watch: Function) => {
    if (!generatePermissionMutation) return;
    
    const userId = watch("id");
    
    if (!userId) {
      toast({
        title: "Erro!",
        description: "ID do usuário não encontrado.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await generatePermissionMutation.mutateAsync(userId);
      
      toast({
        title: "Sucesso!",
        description: result.message || "Geração de permissão finalizada!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      onAlertClose();
      
      if (onRefetchData) {
        await onRefetchData();
      }
      
    } catch (error) {
      toast({
        title: "Erro!",
        description: "Erro ao gerar permissão. Tente novamente.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePrintQRCode = () => {
    const qrCodeContent = qrCodeRef.current?.innerHTML;
    if (qrCodeContent) {
      const printWindow = window.open('', '_blank');
      printWindow?.document.write(`
        <html>
          <head><title>Imprimir QR Code</title></head>
          <body style="text-align: center; margin-top: 50px;">
            ${qrCodeContent}
            <script>
              setTimeout(() => { 
                window.print();
                window.close();
              }, 250);
            </script>
          </body>
        </html>
      `);
      printWindow?.document.close();
    }
  };

  const handleCPFSearch = async (cpf: string, setValue: Function) => {
    // Remove caracteres não numéricos
    const cleanCPF = cpf.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos
    if (cleanCPF.length !== 11) {
      toast({
        title: "CPF inválido",
        description: "Digite um CPF válido com 11 dígitos.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSearchingCPF(true);

    try {
      const response = await api.get(`/usuario/geral/cpf/${cleanCPF}`);
      const userData = response.data;

      // Verifica se o status é 0 (CPF não encontrado)
      if (userData.status === 0) {
        toast({
          title: "CPF não encontrado",
          description: userData.mensagem || "Preencha os dados manualmente.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Preenche os campos com os dados retornados
      if (userData.nome) {
        setValue("nome", userData.nome);
      }
      if (userData.mae) {
        setValue("nomeMae", userData.mae);
      }

      toast({
        title: "Dados encontrados!",
        description: "Os campos foram preenchidos automaticamente.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error: any) {
      toast({
        title: "Erro ao buscar dados",
        description: "Não foi possível buscar os dados do CPF.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearchingCPF(false);
    }
  };

  return (
    <UpsertFormModal<FormValues>
      resolver={zodResolver(edit ? schemaEdit : schema)}
      isLoading={isLoading}
      {...props}
      defaultValues={edit ? props.defaultValues : { ...props.defaultValues, ativo: true }}
      title={title || ""}
      headerExtraContent={(watch, setValue) => (
        <HStack w={"50%"} justify={"flex-end"} pr={10}>
          <Flex alignItems="center" justifyContent="flex-end" width="100%">
            {edit && (
              <Tooltip
                label="Gerenciar permissões de acesso"
                placement="top"
                hasArrow
                bg="gray.700"
              >
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="yellow"
                  onClick={() => nav("../listapermissoes/" + watch("id"))}
                  borderRadius="md"
                  boxShadow="sm"
                  transition="all 0.2s"
                  bg="#ffffff22"
                  color="white"
                  border="1px solid #ffffff55"
                  _hover={{
                    bg: "#ffffff33",
                    borderColor: "white",
                    transform: "translateY(-2px)",
                    boxShadow: "md",
                  }}
                  leftIcon={<Icon as={FaKey} boxSize={4} />}
                >
                  <Text fontWeight="medium">Permissões</Text>
                </Button>
              </Tooltip>
            )}
          </Flex>
          <Flex>
            <ActiveSwitch
              size="sm"
              isChecked={watch("ativo")}
              onChange={() => setValue("ativo", !watch("ativo"))}
            />
          </Flex>
        </HStack>
      )}
    >
      {(register, errors, _, watch, setValue) => {
        // Verifica se o CPF está preenchido (11 dígitos)
        const cpfValue = watch("cpf") || "";
        const cleanCPF = cpfValue.replace(/\D/g, '');
        const isCPFFilled = cleanCPF.length > 0;
        const shouldDisableFields = !edit && !isCPFFilled;

        return (
        <>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem>
              <FormControl isInvalid={!!errors.cpf}>
                <FormLabel color="GrayText" fontSize={"sm"}>
                  *CPF:
                </FormLabel>
                <InputMask
                  {...register("cpf")}
                  value={watch("cpf") || ""}
                  mask={"999.999.999-99"}
                  defaultValue={String(
                    props.defaultValues?.cpf || ""
                  )}
                  disabled={edit || isSearchingCPF}
                  onBlur={(e) => {
                    if (!edit) {
                      handleCPFSearch(e.target.value, setValue);
                    }
                  }}
                >
                  {/* @ts-ignore */}
                  {(inputProps) => (
                    <Input
                      size={"sm"}
                      borderRadius={"md"}
                      bg={"#f4f4f8"}
                      {...inputProps}
                    />
                  )}
                </InputMask>
                <FieldError error={errors.cpf} />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={!!errors.nome}>
                <FormLabel color="GrayText" fontSize={"sm"}>
                  *Nome:
                </FormLabel>
                <Input
                  size={"sm"}
                  borderRadius={"md"}
                  bg={"#f4f4f8"}
                  {...register("nome")}
                  disabled={shouldDisableFields || isSearchingCPF}
                />
                <FieldError error={errors.nome} />
              </FormControl>
            </GridItem>
          </Grid>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem>
              <FormControl isInvalid={!!errors.login}>
                <FormLabel color="GrayText" fontSize={"sm"}>
                  *Login:
                </FormLabel>
                <Input
                  size={"sm"}
                  borderRadius={"md"}
                  bg={"#f4f4f8"}
                  type="email"
                  {...register("login")}
                  defaultValue={"@" + email}
                  disabled={shouldDisableFields || isSearchingCPF}
                />
                <FieldError error={errors.login} />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={!!errors.senha}>
                <FormLabel color="GrayText" fontSize={"sm"}>
                  {edit ? "Senha:" : "*Senha:"}
                </FormLabel>
                <Input
                  size={"sm"}
                  borderRadius={"md"}
                  bg={"#f4f4f8"}
                  type="password"
                  placeholder={edit ? "Deixe em branco para manter a senha atual" : ""}
                  {...register("senha")}
                  disabled={shouldDisableFields || isSearchingCPF}
                />
                <FieldError error={errors.senha} />
              </FormControl>
            </GridItem>
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel color="GrayText" fontSize={"sm"}>
                  *E-mail:
                </FormLabel>
                <Input
                  size={"sm"}
                  borderRadius={"md"}
                  bg={"#f4f4f8"}
                  type="email"
                  {...register("email", { setValueAs: (v) => v })}
                  defaultValue={"@" + email}
                  disabled={shouldDisableFields || isSearchingCPF}
                />
                <FieldError error={errors.email} />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={!!errors.telefone}>
                <FormLabel color="GrayText" fontSize={"sm"}>
                  Telefone:
                </FormLabel>
                <InputMask
                  {...register("telefone")}
                  value={watch("telefone") || ""}
                  mask={"(99) 99999-9999"}
                  defaultValue={props.defaultValues?.telefone}
                  disabled={shouldDisableFields || isSearchingCPF}
                >
                  {/* @ts-ignore */}
                  {(inputProps) => (
                    <Input
                      size={"sm"}
                      borderRadius={"md"}
                      bg={"#f4f4f8"}
                      {...inputProps}
                    />
                  )}
                </InputMask>
                <FieldError error={errors.telefone} />
              </FormControl>
            </GridItem>
          </Grid>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem>
              <FormControl isInvalid={!!errors.nomeMae}>
                <FormLabel color="GrayText" fontSize={'sm'}>Nome da Mãe:</FormLabel>
                <Input 
                  size={'sm'} 
                  borderRadius={'md'} 
                  bg={'#f4f4f8'}  
                  {...register("nomeMae")} 
                  disabled={shouldDisableFields || isSearchingCPF}
                />
                <FieldError error={errors.nomeMae} />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={!!errors.idEndereco}>
                <FormLabel color="GrayText" fontSize={'sm'}>* Endereço Usuário:</FormLabel>
                <Input 
                  size={'sm'} 
                  borderRadius={'md'} 
                  bg={'#f4f4f8'}  
                  type="number"
                  {...register("idEndereco", { valueAsNumber: true })} 
                  disabled={shouldDisableFields || isSearchingCPF}
                />
                <FieldError error={errors.idEndereco} />
              </FormControl>
            </GridItem>
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem>
              <FormControl isInvalid={!!errors.codigo}>
                <FormLabel color="GrayText" fontSize={"sm"}>
                  Código:
                </FormLabel>
                <Input
                  size={"sm"}
                  borderRadius={"md"}
                  bg={"#f4f4f8"}
                  {...register("codigo")}
                  disabled={shouldDisableFields || isSearchingCPF}
                />
                <FieldError error={errors.codigo} />
              </FormControl>
            </GridItem>

            {edit && (
              <GridItem>
                <FormControl isInvalid={!!errors.codigoPermissao}>
                  <FormLabel color="GrayText" fontSize={"sm"}>
                    Codigo de Permissão:
                  </FormLabel>
                  <InputGroup size="sm">
                    <Input
                      size={"sm"}
                      borderRadius={"md"}
                      bg={"#f4f4f8"}
                      {...register("codigoPermissao")}
                      isReadOnly
                      placeholder="Codigo de Permissão será gerado automaticamente"
                    />
                    <InputRightAddon>
                      <HStack>
                        <Tooltip
                          label="Gerar novo codigo Permissao"
                          placement="top"
                          hasArrow
                        >
                          <IconButton
                            aria-label="Gerar novo codigoPermissao"
                            icon={<IoIosToday />}
                            onClick={onAlertOpen}
                            color={"white"}
                            bg={"rgba(17, 123, 170, 1)"}
                            h={"30px"}
                            isLoading={
                              generatePermissionMutation?.isLoading || false
                            }
                          />
                        </Tooltip>
                        <Tooltip
                          label="Imprimir QR Code"
                          placement="top"
                          hasArrow
                        >
                          <IconButton
                            aria-label="Imprimir QR Code"
                            icon={<IoMdPrint />}
                            color={"white"}
                            onClick={onQrModalOpen}
                            isDisabled={!watch("codigoPermissao")}
                            bg={"rgba(56, 161, 105, 1)"}
                            h={"30px"}
                          />
                        </Tooltip>
                      </HStack>
                    </InputRightAddon>
                  </InputGroup>
                  <FieldError error={errors.codigoPermissao} />
                </FormControl>
              </GridItem>
            )}
          </Grid>

          <AlertDialog
            isOpen={isAlertOpen}
            leastDestructiveRef={cancelRef}
            onClose={onAlertClose}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader p={0} m={0} fontSize="lg" fontWeight="bold">
                  <Heading
                    position="relative"
                    left={7}
                    top={-4}
                    as="h3"
                    size="sm"
                    color="white"
                    w={"fit-content"}
                    px={0}
                    py={0}
                    borderRadius="md"
                  >
                    <Flex position="relative" alignItems="center" h="40px">
                      <Box
                        position="absolute"
                        left="20px"
                        zIndex={1}
                        bg="rgba(17, 123, 170, 1)"
                        borderRightRadius={"2xl"}
                        py={1}
                        w={"100px"}
                        pl="20px"
                        pr={4}
                      >
                        <Text fontWeight="bold" color="white" fontSize={"base"}>
                          AVISO
                        </Text>
                      </Box>

                      <Box
                        as={FiAlertTriangle}
                        bg="red.500"
                        color="white"
                        p="1"
                        borderRadius="full"
                        w="30px"
                        h="30px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        zIndex={2}
                        position="relative"
                      />
                    </Flex>
                  </Heading>
                </AlertDialogHeader>
                <AlertDialogBody>
                  Você tem certeza que deseja gerar uma nova permissão? Isso irá
                  atualizar os dados do usuário.
                </AlertDialogBody>
                <AlertDialogFooter justifyContent={"center"}>
                  <HStack spacing={4} justifyContent="center">
                    <Button
                      w={"100px"}
                      colorScheme="green"
                      size={"md"}
                      onClick={() => handleGeneratecodigoPermissao(watch)}
                      ml={3}
                      isLoading={generatePermissionMutation?.isLoading || false}
                    >
                      Sim
                    </Button>
                    <Button
                      w={"100px"}
                      colorScheme="red"
                      ref={cancelRef}
                      size={"md"}
                      onClick={onAlertClose}
                    >
                      Não
                    </Button>
                  </HStack>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

          <Modal isOpen={isQrModalOpen} onClose={onQrModalClose} isCentered>
            <ModalOverlay />
            <ModalContent w={"200px"}>
              <ModalBody
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
              >
                <Box ref={qrCodeRef}>
                  <QRCodeSVG height="200px" value={watch("codigoPermissao") || ""} />
                </Box>
              </ModalBody>
              <ModalFooter p={0} py={2} m={0} justifyContent={"center"}>
                <Button
                  colorScheme="blue"
                  leftIcon={<FaPrint />}
                  onClick={handlePrintQRCode}
                >
                  Imprimir QR Code
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
        );
      }}
    </UpsertFormModal>
  );
}